import React, { useState, useEffect, useRef } from 'react';
import {
  X, Play, Square, Shield, Server, Network, Key, Crown,
  Database, Lock, CheckCircle, XCircle, Loader2, Clock,
  ChevronDown, ChevronUp, Brain, AlertTriangle, Eye, EyeOff,
  Target, Zap, Activity, Terminal, Bot, Info, Settings,
  ChevronRight, RotateCcw, BookOpen, Users, KeyRound, Upload,
  List, Plus, Trash2, FileText, FolderTree, Award, HelpCircle
} from 'lucide-react';
import MarkdownRenderer from '../common/MarkdownRenderer';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const Tooltip = ({ text, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const posClasses = {
    top:    'bottom-full left-1/2 -translate-x-1/2 mb-2',
    right:  'left-full top-1/2 -translate-y-1/2 ml-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left:   'right-full top-1/2 -translate-y-1/2 mr-2',
  };
  const arrowClasses = {
    top:    'top-full left-1/2 -translate-x-1/2 border-t-gray-700',
    right:  'right-full top-1/2 -translate-y-1/2 border-r-gray-700',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-700',
    left:   'left-full top-1/2 -translate-y-1/2 border-l-gray-700',
  };
  return (
    <span className="relative inline-flex" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <HelpCircle className="w-3.5 h-3.5 text-gray-500 hover:text-gray-300 cursor-help transition-colors" />
      {visible && (
        <span className={`absolute z-50 ${posClasses[position]} w-64 p-2.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 leading-relaxed shadow-xl pointer-events-none`}>
          {text}
          <span className={`absolute border-4 border-transparent ${arrowClasses[position]}`} />
        </span>
      )}
    </span>
  );
};

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

// Phase icons mapping
const phaseIcons = {
  reconnaissance:       Target,
  enumeration:          Database,
  credential_harvesting: Key,
  secrets_dump:         Database,
  lateral_movement:     Network,
  privilege_escalation: Crown,
  persistence:          Lock,
  adcs:                 Award,
  coercion:             Zap,
  relay:                Activity,
  delegation:           KeyRound,
  trust_abuse:          FolderTree,
  sccm:                 Server,
  exfiltration:         Shield,
};

// French labels for phases
const phaseLabels = {
  reconnaissance:       'Reconnaissance',
  enumeration:          'Énumération',
  credential_harvesting: 'Collecte Credentials',
  secrets_dump:         'Extraction Secrets',
  lateral_movement:     'Mouvement Latéral',
  privilege_escalation: 'Escalade Privilèges',
  persistence:          'Persistance',
  adcs:                 'ADCS / PKI',
  coercion:             'Coercion Auth.',
  relay:                'NTLM Relay',
  delegation:           'Délégation Kerberos',
  trust_abuse:          'Trust Abuse / Forêt',
  sccm:                 'SCCM / MECM',
  exfiltration:         'Exfiltration',
};

// Phase colors
const phaseColors = {
  reconnaissance:       'blue',
  enumeration:          'purple',
  credential_harvesting: 'yellow',
  secrets_dump:         'green',
  lateral_movement:     'orange',
  privilege_escalation: 'red',
  persistence:          'gray',
  adcs:                 'pink',
  coercion:             'orange',
  relay:                'red',
  delegation:           'blue',
  trust_abuse:          'purple',
  sccm:                 'gray',
  exfiltration:         'green',
};

const getPhaseColorClasses = (phase) => {
  const colors = {
    blue:   'bg-blue-500/20 border-blue-500/50 text-blue-400',
    purple: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
    yellow: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400',
    orange: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
    red:    'bg-red-500/20 border-red-500/50 text-red-400',
    gray:   'bg-gray-500/20 border-gray-500/50 text-gray-400',
    green:  'bg-green-500/20 border-green-500/50 text-green-400',
    pink:   'bg-pink-500/20 border-pink-500/50 text-pink-400',
  };
  return colors[phaseColors[phase]] || colors.blue;
};

// Status icon component
const StatusIcon = ({ status, className = "w-5 h-5" }) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className={`${className} text-green-400`} />;
    case 'failed':
      return <XCircle className={`${className} text-red-400`} />;
    case 'running':
      return <Loader2 className={`${className} text-blue-400 animate-spin`} />;
    case 'skipped':
      return <AlertTriangle className={`${className} text-yellow-400`} />;
    default:
      return <Clock className={`${className} text-gray-500`} />;
  }
};

const ADAttackWizard = ({ open, isOpen, onClose, initialDomain, initialDcIp, initialCredentials, workspaceId }) => {
  // Support both 'open' and 'isOpen' props
  const isVisible = open ?? isOpen ?? false;
  const pollInterval = useRef(null);

  // Form state
  const [domain, setDomain] = useState(initialDomain || '');
  const [dcIp, setDcIp] = useState(initialDcIp || '');
  const [username, setUsername] = useState(initialCredentials?.username || '');
  const [password, setPassword] = useState(initialCredentials?.password || '');
  const [hash, setHash] = useState(initialCredentials?.hash || '');
  const [showPassword, setShowPassword] = useState(false);
  const [skipSteps, setSkipSteps] = useState([]);

  // Wordlists state
  const [userlist, setUserlist] = useState([]);
  const [passlist, setPasslist] = useState([]);
  const [customUsername, setCustomUsername] = useState('');
  const [customPassword, setCustomPassword] = useState('');
  const [availableWordlists, setAvailableWordlists] = useState({ usernames: [], passwords: [] });
  const [showWordlistSection, setShowWordlistSection] = useState(false);

  // Execution mode
  const [simulationMode, setSimulationMode] = useState(false);

  // LDAP Options state
  const [showLdapOptions, setShowLdapOptions] = useState(false);
  const [ldapOptions, setLdapOptions] = useState({
    include_ldap_scan: true,
    ldap_port: 389,
    ldaps_port: 636,
    check_anonymous_bind: true,
    check_certificates: true,
    check_delegation: true,
    run_bloodhound: false
  });

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [attackChain, setAttackChain] = useState([]);

  // UI state
  const [expandedStep, setExpandedStep] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [showConfig, setShowConfig] = useState(true);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Load attack chain and wordlists on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [chainResponse, wordlistsResponse] = await Promise.all([
          apiCall('/api/v1/ad-wizard/attack-chain'),
          apiCall('/api/v1/ad-wizard/wordlists')
        ]);
        setAttackChain(chainResponse.attack_chain || []);
        setAvailableWordlists(wordlistsResponse);
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    if (isVisible) {
      loadData();
    }
  }, [isVisible]);

  // Poll session status
  useEffect(() => {
    if (sessionId && isRunning) {
      pollInterval.current = setInterval(async () => {
        try {
          const response = await apiCall(`/api/v1/ad-wizard/sessions/${sessionId}/status`);
          setSessionStatus(response);

          // Check if completed
          if (response.status === 'completed' || response.status === 'stopped') {
            setIsRunning(false);
            clearInterval(pollInterval.current);
            showNotification('AD Attack Wizard terminé!', 'success');
          }
        } catch (error) {
          console.error('Failed to poll status:', error);
        }
      }, 2000);
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [sessionId, isRunning]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isVisible) {
      setSessionId(null);
      setSessionStatus(null);
      setIsRunning(false);
      setExpandedStep(null);
      setShowConfig(true);
    }
  }, [isVisible]);

  // Update form when props change
  useEffect(() => {
    if (initialDomain) setDomain(initialDomain);
    if (initialDcIp) setDcIp(initialDcIp);
    if (initialCredentials?.username) setUsername(initialCredentials.username);
    if (initialCredentials?.password) setPassword(initialCredentials.password);
    if (initialCredentials?.hash) setHash(initialCredentials.hash);
  }, [initialDomain, initialDcIp, initialCredentials]);

  // Start wizard
  const handleStart = async () => {
    if (!dcIp) {
      showNotification('L\'IP du Domain Controller est requise', 'error');
      return;
    }

    try {
      const response = await apiCall('/api/v1/ad-wizard/start', {
        method: 'POST',
        body: JSON.stringify({
          target_domain: domain,
          dc_ip: dcIp,
          username: username || null,
          password: password || null,
          hash: hash || null,
          skip_steps: skipSteps,
          userlist: userlist,
          passlist: passlist,
          workspace_id: workspaceId || null,
          use_vpn: true,
          simulation_mode: simulationMode,
        }),
      });

      if (response.success) {
        setSessionId(response.session_id);
        setIsRunning(true);
        setShowConfig(false);
        showNotification('AD Attack Wizard démarré!', 'success');
      }
    } catch (error) {
      showNotification(`Erreur: ${error.message}`, 'error');
    }
  };

  // Stop wizard
  const handleStop = async () => {
    if (!sessionId) return;

    try {
      await apiCall(`/api/v1/ad-wizard/sessions/${sessionId}/stop`, { method: 'POST' });
      setIsRunning(false);
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
      showNotification('Wizard arrêté', 'warning');
    } catch (error) {
      showNotification(`Erreur: ${error.message}`, 'error');
    }
  };

  // Toggle step skip
  const toggleSkipStep = (stepId) => {
    setSkipSteps(prev =>
      prev.includes(stepId)
        ? prev.filter(id => id !== stepId)
        : [...prev, stepId]
    );
  };

  // Wordlist management functions
  const loadPredefinedWordlist = async (type, listId) => {
    try {
      const response = await apiCall(`/api/v1/ad-wizard/wordlists/${type}/${listId}`);
      if (type === 'usernames') {
        setUserlist(prev => [...new Set([...prev, ...response.words])]);
      } else {
        setPasslist(prev => [...new Set([...prev, ...response.words])]);
      }
      showNotification(`${response.count} entrées ajoutées depuis "${response.name}"`, 'success');
    } catch (error) {
      showNotification(`Erreur: ${error.message}`, 'error');
    }
  };

  const addCustomUsername = () => {
    if (customUsername.trim() && !userlist.includes(customUsername.trim())) {
      setUserlist(prev => [...prev, customUsername.trim()]);
      setCustomUsername('');
    }
  };

  const addCustomPassword = () => {
    if (customPassword.trim() && !passlist.includes(customPassword.trim())) {
      setPasslist(prev => [...prev, customPassword.trim()]);
      setCustomPassword('');
    }
  };

  const removeFromUserlist = (item) => {
    setUserlist(prev => prev.filter(i => i !== item));
  };

  const removeFromPasslist = (item) => {
    setPasslist(prev => prev.filter(i => i !== item));
  };

  const handleFileUpload = (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const lines = content.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (type === 'usernames') {
        setUserlist(prev => [...new Set([...prev, ...lines])]);
      } else {
        setPasslist(prev => [...new Set([...prev, ...lines])]);
      }
      showNotification(`${lines.length} entrées importées depuis le fichier`, 'success');
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // Calculate progress
  const getProgress = () => {
    if (!sessionStatus?.steps) return 0;
    const completed = sessionStatus.steps.filter(s => ['completed', 'failed', 'skipped'].includes(s.status)).length;
    return Math.round((completed / sessionStatus.steps.length) * 100);
  };

  // Get findings count
  const getFindingsCount = () => {
    if (!sessionStatus?.steps) return 0;
    return sessionStatus.steps.reduce((acc, step) => acc + (step.findings?.length || 0), 0);
  };

  // Group attack chain by phase
  const getAttackChainByPhase = () => {
    const grouped = {};
    attackChain.forEach(step => {
      if (!grouped[step.phase]) {
        grouped[step.phase] = [];
      }
      grouped[step.phase].push(step);
    });
    return grouped;
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={!isRunning ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-[95vw] h-[90vh] bg-brand-black rounded-2xl border border-brand-green/30 shadow-2xl shadow-brand-green/10 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-green/20 bg-brand-black-light">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-brand-green/20 border border-brand-green/30">
              <Shield className="w-6 h-6 text-brand-green" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gradient-green">AD Attack Wizard</h2>
              <p className="text-sm text-gray-400">
                Test de pénétration Active Directory automatisé avec analyse IA
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {sessionStatus && (
              <div className="flex items-center gap-4 mr-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-black rounded-lg border border-gray-700">
                  <Activity className="w-4 h-4 text-brand-green" />
                  <span className="text-sm text-white">{getProgress()}%</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-black rounded-lg border border-red-500/30">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400">{getFindingsCount()} findings</span>
                </div>
              </div>
            )}

            {isRunning && (
              <span className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg border border-blue-500/30">
                <Loader2 className="w-4 h-4 animate-spin" />
                En cours...
              </span>
            )}

            <button
              onClick={!isRunning ? onClose : undefined}
              disabled={isRunning}
              className={`p-2 rounded-lg transition-colors ${
                isRunning
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mx-6 mt-4 p-3 rounded-lg border flex items-center gap-3 ${
            notification.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' :
            notification.type === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
            notification.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' :
            'bg-blue-500/10 border-blue-500/30 text-blue-400'
          }`}>
            {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {notification.type === 'error' && <XCircle className="w-5 h-5" />}
            {notification.type === 'warning' && <AlertTriangle className="w-5 h-5" />}
            {notification.type === 'info' && <Info className="w-5 h-5" />}
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Configuration or Timeline */}
          <div className="w-1/3 border-r border-brand-green/20 flex flex-col overflow-hidden">
            {/* Configuration Section */}
            {!sessionId && (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Server className="w-5 h-5 text-brand-green" />
                    Configuration Cible
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-400 mb-2">
                        Domaine AD <span className="text-gray-600 font-normal">(optionnel)</span>
                        <Tooltip position="right" text="Nom FQDN du domaine Active Directory cible (ex: corp.local). Utilisé pour les requêtes LDAP, Kerberos et BloodHound. Si vide, les techniques d'énumération feront de l'auto-découverte via le DC." />
                      </label>
                      <input
                        type="text"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="corp.local (auto-découverte si vide)"
                        className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-400 mb-2">
                        IP Domain Controller <span className="text-red-500">*</span>
                        <Tooltip position="right" text="Adresse IP du contrôleur de domaine principal (DC). C'est le serveur LDAP/Kerberos qui sera ciblé. Obligatoire pour toutes les attaques AD." />
                      </label>
                      <input
                        type="text"
                        value={dcIp}
                        onChange={(e) => setDcIp(e.target.value)}
                        placeholder="192.168.1.10"
                        className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                      />
                    </div>

                    <div className="pt-4 border-t border-gray-700">
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Identifiants (Optionnel)
                        <Tooltip position="right" text="Sans identifiants, seules les attaques anonymes sont lancées (null session, AS-REP Roasting, énumération SMB). Avec un compte valide, les 71 techniques OCD sont toutes exploitables." />
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-1">
                            Nom d'utilisateur
                            <Tooltip position="right" text="Compte AD valide (ex: jdoe ou DOMAIN\jdoe). Déverrouille les attaques authentifiées : Kerberoasting, BloodHound, LDAP dump, DCSync, mouvement latéral." />
                          </label>
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="admin"
                            className="w-full px-4 py-2.5 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green text-sm"
                          />
                        </div>

                        <div>
                          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-1">
                            Mot de passe
                            <Tooltip position="right" text="Mot de passe en clair du compte. Peut être laissé vide si vous fournissez un NT Hash. Utilisé pour l'authentification LDAP, Kerberos et les connexions SMB/WinRM." />
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="********"
                              className="w-full px-4 py-2.5 pr-10 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-1">
                            NT Hash (Pass-the-Hash)
                            <Tooltip position="right" text="Hash NTLM au format LM:NT ou simplement :NT (ex: aad3b435b51404eeaad3b435b51404ee:31d6cfe0...). Permet l'authentification sans mot de passe en clair — technique courante après extraction de la SAM ou LSASS." />
                          </label>
                          <input
                            type="text"
                            value={hash}
                            onChange={(e) => setHash(e.target.value)}
                            placeholder="aad3b435b51404eeaad3b435b51404ee:..."
                            className="w-full px-4 py-2.5 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green text-sm font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* LDAP Options Section */}
                <div>
                  <button
                    onClick={() => setShowLdapOptions(!showLdapOptions)}
                    className="w-full flex items-center justify-between text-lg font-bold text-white mb-4"
                  >
                    <span className="flex items-center gap-2">
                      <FolderTree className="w-5 h-5 text-cyan-400" />
                      Options LDAP/LDAPS
                      <Tooltip position="right" text="Configure un scan LDAP approfondi en parallèle de l'attaque. Permet l'énumération des objets AD, la détection de délégations dangereuses, l'analyse des certificats (ADCS/ESC1-8) et la collecte BloodHound." />
                    </span>
                    <div className="flex items-center gap-2">
                      {ldapOptions.include_ldap_scan && (
                        <span className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">
                          Actif
                        </span>
                      )}
                      {showLdapOptions ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </button>

                  {showLdapOptions && (
                    <div className="space-y-4 p-4 bg-brand-black rounded-lg border border-cyan-500/20">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ldapOptions.include_ldap_scan}
                          onChange={(e) => setLdapOptions({...ldapOptions, include_ldap_scan: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-600 bg-brand-black text-cyan-500 focus:ring-cyan-500"
                        />
                        <span className="text-white">Inclure scan LDAP dans l'attaque</span>
                      </label>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Port LDAP</label>
                          <input
                            type="number"
                            value={ldapOptions.ldap_port}
                            onChange={(e) => setLdapOptions({...ldapOptions, ldap_port: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Port LDAPS</label>
                          <input
                            type="number"
                            value={ldapOptions.ldaps_port}
                            onChange={(e) => setLdapOptions({...ldapOptions, ldaps_port: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white text-sm"
                          />
                        </div>
                      </div>

                      <div className="space-y-2 pt-3 border-t border-gray-700">
                        <p className="text-xs text-gray-400 mb-2">Tests LDAP à inclure :</p>
                        {[
                          { key: 'check_anonymous_bind', label: 'Test Anonymous Bind', desc: 'Vérification du bind LDAP anonyme' },
                          { key: 'check_certificates', label: 'Analyse Certificats (ESC1-ESC8)', desc: 'Détection des templates vulnérables' },
                          { key: 'check_delegation', label: 'Vérification Délégation', desc: 'Détection des problèmes de délégation' },
                          { key: 'run_bloodhound', label: 'Collection BloodHound', desc: 'Collecte complète pour BloodHound' }
                        ].map(opt => (
                          <label key={opt.key} className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-gray-800/50">
                            <input
                              type="checkbox"
                              checked={ldapOptions[opt.key]}
                              onChange={(e) => setLdapOptions({...ldapOptions, [opt.key]: e.target.checked})}
                              className="w-4 h-4 mt-0.5 rounded border-gray-600 bg-brand-black text-cyan-500 focus:ring-cyan-500"
                            />
                            <div>
                              <span className="text-sm text-white">{opt.label}</span>
                              <p className="text-xs text-gray-500">{opt.desc}</p>
                            </div>
                          </label>
                        ))}
                      </div>

                      <div className="pt-3 border-t border-gray-700">
                        <div className="flex items-center gap-2 text-xs text-cyan-400">
                          <Award className="w-4 h-4" />
                          <span>Les resultats LDAP seront disponibles dans l'onglet LDAP/LDAPS Pentest</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Wordlists Section */}
                <div>
                  <button
                    onClick={() => setShowWordlistSection(!showWordlistSection)}
                    className="w-full flex items-center justify-between text-lg font-bold text-white mb-4"
                  >
                    <span className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-brand-green" />
                      Dictionnaires (Optionnel)
                      <Tooltip position="right" text="Listes de noms d'utilisateurs et mots de passe pour le password spraying et la bruteforce. Sans liste, le wizard utilise des listes intégrées communes (rockyou top 100, utilisateurs AD standards)." />
                    </span>
                    <div className="flex items-center gap-2">
                      {(userlist.length > 0 || passlist.length > 0) && (
                        <span className="text-xs px-2 py-0.5 bg-brand-green/20 text-brand-green rounded">
                          {userlist.length + passlist.length} entrées
                        </span>
                      )}
                      {showWordlistSection ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </button>

                  {showWordlistSection && (
                    <div className="space-y-4">
                      {/* Usernames Section */}
                      <div className="p-4 bg-brand-black rounded-lg border border-gray-800">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-medium text-white flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-400" />
                            Liste d'utilisateurs
                            <span className="text-xs text-gray-500">({userlist.length})</span>
                            <Tooltip position="right" text="Noms d'utilisateurs à tester (un par ligne). Utilisés pour le password spraying (1 mdp sur N comptes), l'AS-REP Roasting et l'énumération LDAP ciblée. Format : jdoe ou jdoe@corp.local" />
                          </h5>
                          <label className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 text-gray-400 rounded cursor-pointer hover:bg-gray-700 hover:text-white transition-colors">
                            <Upload className="w-3 h-3" />
                            Importer
                            <input
                              type="file"
                              accept=".txt,.csv"
                              onChange={(e) => handleFileUpload('usernames', e)}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {/* Predefined lists dropdown */}
                        <div className="mb-3">
                          <select
                            onChange={(e) => e.target.value && loadPredefinedWordlist('usernames', e.target.value)}
                            className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-sm text-white focus:border-brand-green"
                            defaultValue=""
                          >
                            <option value="">+ Ajouter une liste prédéfinie...</option>
                            {availableWordlists.usernames?.map(list => (
                              <option key={list.id} value={list.id}>
                                {list.name} ({list.count} entrées)
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Add custom username */}
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            value={customUsername}
                            onChange={(e) => setCustomUsername(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addCustomUsername()}
                            placeholder="Ajouter un utilisateur..."
                            className="flex-1 px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-sm text-white placeholder-gray-400 focus:border-brand-green"
                          />
                          <button
                            onClick={addCustomUsername}
                            disabled={!customUsername.trim()}
                            className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        {/* User list preview */}
                        {userlist.length > 0 && (
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {userlist.slice(0, 10).map((user, i) => (
                              <div key={i} className="flex items-center justify-between px-2 py-1 bg-gray-800/50 rounded text-xs">
                                <span className="text-gray-300 font-mono">{user}</span>
                                <button
                                  onClick={() => removeFromUserlist(user)}
                                  className="text-gray-500 hover:text-red-400 transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            {userlist.length > 10 && (
                              <div className="text-xs text-gray-500 text-center py-1">
                                + {userlist.length - 10} autres...
                              </div>
                            )}
                          </div>
                        )}

                        {userlist.length > 0 && (
                          <button
                            onClick={() => setUserlist([])}
                            className="mt-2 text-xs text-red-400 hover:text-red-300"
                          >
                            Tout effacer
                          </button>
                        )}
                      </div>

                      {/* Passwords Section */}
                      <div className="p-4 bg-brand-black rounded-lg border border-gray-800">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-medium text-white flex items-center gap-2">
                            <KeyRound className="w-4 h-4 text-yellow-400" />
                            Liste de mots de passe
                            <span className="text-xs text-gray-500">({passlist.length})</span>
                            <Tooltip position="right" text="Mots de passe à tester lors du password spraying. Attention : trop de tentatives peut verrouiller les comptes (lockout). Préférez des listes courtes (top 10-50) et espacées dans le temps." />
                          </h5>
                          <label className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 text-gray-400 rounded cursor-pointer hover:bg-gray-700 hover:text-white transition-colors">
                            <Upload className="w-3 h-3" />
                            Importer
                            <input
                              type="file"
                              accept=".txt,.csv"
                              onChange={(e) => handleFileUpload('passwords', e)}
                              className="hidden"
                            />
                          </label>
                        </div>

                        {/* Predefined lists dropdown */}
                        <div className="mb-3">
                          <select
                            onChange={(e) => e.target.value && loadPredefinedWordlist('passwords', e.target.value)}
                            className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-sm text-white focus:border-brand-green"
                            defaultValue=""
                          >
                            <option value="">+ Ajouter une liste prédéfinie...</option>
                            {availableWordlists.passwords?.map(list => (
                              <option key={list.id} value={list.id}>
                                {list.name} ({list.count} entrées)
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Add custom password */}
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            value={customPassword}
                            onChange={(e) => setCustomPassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addCustomPassword()}
                            placeholder="Ajouter un mot de passe..."
                            className="flex-1 px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-sm text-white placeholder-gray-400 focus:border-brand-green"
                          />
                          <button
                            onClick={addCustomPassword}
                            disabled={!customPassword.trim()}
                            className="px-3 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors disabled:opacity-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Password list preview */}
                        {passlist.length > 0 && (
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {passlist.slice(0, 10).map((pass, i) => (
                              <div key={i} className="flex items-center justify-between px-2 py-1 bg-gray-800/50 rounded text-xs">
                                <span className="text-gray-300 font-mono">{pass}</span>
                                <button
                                  onClick={() => removeFromPasslist(pass)}
                                  className="text-gray-500 hover:text-red-400 transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            {passlist.length > 10 && (
                              <div className="text-xs text-gray-500 text-center py-1">
                                + {passlist.length - 10} autres...
                              </div>
                            )}
                          </div>
                        )}

                        {passlist.length > 0 && (
                          <button
                            onClick={() => setPasslist([])}
                            className="mt-2 text-xs text-red-400 hover:text-red-300"
                          >
                            Tout effacer
                          </button>
                        )}
                      </div>

                      {/* Info box */}
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-blue-300">
                            Les dictionnaires seront utilisés pour le Password Spraying,
                            l'énumération d'utilisateurs et les attaques brute-force.
                            Vous pouvez importer des fichiers .txt (un élément par ligne)
                            ou utiliser les listes prédéfinies.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Attack Chain Selection */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Settings className="w-5 h-5 text-brand-green" />
                      Chaîne d'Attaque
                      <span className="text-sm font-normal text-gray-500 ml-1">
                        ({attackChain.length - skipSteps.length}/{attackChain.length} actives)
                      </span>
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSkipSteps([])}
                        className="text-xs px-3 py-1 rounded-lg bg-brand-green/10 border border-brand-green/30 text-brand-green hover:bg-brand-green/20 transition-colors"
                      >
                        Tout activer
                      </button>
                      <button
                        onClick={() => setSkipSteps(attackChain.map(s => s.id))}
                        className="text-xs px-3 py-1 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:bg-gray-700 transition-colors"
                      >
                        Tout désactiver
                      </button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 mb-4">
                    Sélectionnez les étapes à exécuter. Toutes sont activées par défaut.
                  </p>

                  <div className="space-y-2">
                    {Object.entries(getAttackChainByPhase()).map(([phase, steps]) => {
                      const PhaseIcon = phaseIcons[phase] || Shield;
                      const enabledCount = steps.filter(s => !skipSteps.includes(s.id)).length;

                      return (
                        <div key={phase} className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                          <button
                            onClick={() => setExpandedPhase(expandedPhase === phase ? null : phase)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-1.5 rounded-lg ${getPhaseColorClasses(phase)}`}>
                                <PhaseIcon className="w-4 h-4" />
                              </div>
                              <span className="text-white font-medium">
                                {phaseLabels[phase] || phase.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium ${enabledCount === 0 ? 'text-gray-600' : enabledCount === steps.length ? 'text-brand-green' : 'text-yellow-400'}`}>
                                {enabledCount}/{steps.length}
                              </span>
                              {expandedPhase === phase ? (
                                <ChevronUp className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              )}
                            </div>
                          </button>

                          {expandedPhase === phase && (
                            <div className="px-4 pb-3 space-y-1">
                              <div className="flex gap-2 mb-2">
                                <button
                                  onClick={e => { e.preventDefault(); setSkipSteps(prev => prev.filter(id => !steps.map(s => s.id).includes(id))); }}
                                  className="text-xs px-2 py-0.5 rounded bg-brand-green/10 border border-brand-green/30 text-brand-green hover:bg-brand-green/20 transition-colors"
                                >tout activer</button>
                                <button
                                  onClick={e => { e.preventDefault(); setSkipSteps(prev => [...new Set([...prev, ...steps.map(s => s.id)])]); }}
                                  className="text-xs px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-500 hover:bg-gray-700 transition-colors"
                                >tout désactiver</button>
                              </div>
                              {steps.map(step => (
                                <label
                                  key={step.id}
                                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800/50 cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={!skipSteps.includes(step.id)}
                                    onChange={() => toggleSkipStep(step.id)}
                                    className="w-4 h-4 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white truncate">{step.name}</div>
                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                      <span>{step.tool}</span>
                                      <span className="text-gray-700">•</span>
                                      <span className="text-red-400">{step.mitre_id}</span>
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Execution Mode Toggle */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-brand-green" />
                    Mode d'Exécution
                  </h3>

                  <div className="p-4 bg-brand-black rounded-lg border border-gray-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {simulationMode ? 'Mode Simulation' : 'Mode Exécution Réelle'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {simulationMode
                            ? 'Outputs fictifs — aucun outil lancé réellement'
                            : 'Les outils AD seront exécutés contre la cible'}
                        </div>
                      </div>
                      <button
                        onClick={() => setSimulationMode(prev => !prev)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          simulationMode ? 'bg-gray-600' : 'bg-brand-green'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            simulationMode ? 'translate-x-1' : 'translate-x-6'
                          }`}
                        />
                      </button>
                    </div>

                    {!simulationMode && (
                      <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/40 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-red-300 leading-relaxed">
                          <span className="font-semibold">Attention — Mode Réel :</span> Les outils AD (nxc, impacket, ldapsearch, bloodhound…) seront exécutés réellement contre la cible <span className="font-mono">{dcIp || '...'}</span>.
                          Assurez-vous d'avoir une autorisation écrite avant de lancer l'attaque.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Timeline Section */}
            {sessionId && sessionStatus?.timeline && (
              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-brand-green" />
                  Timeline
                  <span className="ml-2 text-xs px-2 py-0.5 bg-brand-green/20 text-brand-green rounded">
                    {sessionStatus.timeline.length} événements
                  </span>
                </h3>

                <div className="space-y-3">
                  {sessionStatus.timeline.slice().reverse().map((event, index) => (
                    <div
                      key={index}
                      className="p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
                    >
                      {/* Header with icon, message and time */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {(event.event_type || event.event) === 'step_completed' && <CheckCircle className="w-4 h-4 text-green-400" />}
                          {(event.event_type || event.event) === 'step_started' && <Play className="w-4 h-4 text-blue-400" />}
                          {(event.event_type || event.event) === 'step_failed' && <XCircle className="w-4 h-4 text-red-400" />}
                          {(event.event_type || event.event) === 'finding_discovered' && <AlertTriangle className="w-4 h-4 text-yellow-400" />}
                          {(event.event_type || event.event) === 'session_started' && <Zap className="w-4 h-4 text-brand-green" />}
                          {(event.event_type || event.event) === 'session_completed' && <CheckCircle className="w-4 h-4 text-brand-green" />}
                          {!['step_completed', 'step_started', 'step_failed', 'finding_discovered', 'session_started', 'session_completed'].includes(event.event_type || event.event) && (
                            <Info className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm text-white font-medium">{event.message || event.description || event.event_type || event.event}</p>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {new Date(event.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          {/* AI Explanation */}
                          {event.ai_explanation && (
                            <div className="mt-2 flex items-start gap-2 p-2 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded border border-purple-500/20">
                              <Bot className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <MarkdownRenderer content={event.ai_explanation} variant="compact" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Progress / Results */}
          <div className="flex-1 overflow-y-auto p-6">
            {!sessionId ? (
              /* Before Start - Info Panel */
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-24 h-24 rounded-full bg-brand-green/10 border border-brand-green/30 flex items-center justify-center mb-6">
                  <Shield className="w-12 h-12 text-brand-green" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Prêt à lancer l'attaque AD
                </h3>
                <p className="text-gray-400 max-w-md mb-8">
                  Ce wizard va exécuter automatiquement une série d'attaques sur le domaine Active Directory
                  configuré, avec une analyse IA en temps réel pour chaque étape.
                </p>

                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="p-4 bg-brand-black rounded-xl border border-gray-800">
                    <Target className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-white">Reconnaissance</div>
                    <div className="text-sm text-gray-500">Enumération & Collecte</div>
                  </div>
                  <div className="p-4 bg-brand-black rounded-xl border border-gray-800">
                    <Key className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-white">Identifiants</div>
                    <div className="text-sm text-gray-500">Kerberoast, AS-REP...</div>
                  </div>
                  <div className="p-4 bg-brand-black rounded-xl border border-gray-800">
                    <Crown className="w-8 h-8 text-red-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-white">Escalade</div>
                    <div className="text-sm text-gray-500">DCSync, Golden Ticket...</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <Brain className="w-5 h-5" />
                  <span>Analyse IA avec explications détaillées pour chaque étape</span>
                </div>
              </div>
            ) : (
              /* After Start - Steps Progress */
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Progression globale</span>
                    <span className="text-sm font-bold text-brand-green">{getProgress()}%</span>
                  </div>
                  <div className="h-3 bg-brand-black rounded-full overflow-hidden border border-gray-700">
                    <div
                      className="h-full bg-gradient-to-r from-brand-green to-green-400 transition-all duration-500"
                      style={{ width: `${getProgress()}%` }}
                    />
                  </div>
                </div>

                {/* Steps List */}
                <div className="space-y-3">
                  {sessionStatus?.steps?.map((step, index) => {
                    const PhaseIcon = phaseIcons[step.phase] || Shield;
                    const isExpanded = expandedStep === index;

                    return (
                      <div
                        key={step.id}
                        className={`bg-brand-black rounded-xl border transition-all ${
                          step.status === 'running'
                            ? 'border-blue-500/50 shadow-lg shadow-blue-500/10'
                            : step.status === 'completed'
                            ? 'border-green-500/30'
                            : step.status === 'failed'
                            ? 'border-red-500/30'
                            : 'border-gray-800'
                        }`}
                      >
                        {/* Step Header */}
                        <button
                          onClick={() => setExpandedStep(isExpanded ? null : index)}
                          className="w-full px-4 py-3 flex items-center gap-4"
                        >
                          <StatusIcon status={step.status} />

                          <div className={`p-2 rounded-lg ${getPhaseColorClasses(step.phase)}`}>
                            <PhaseIcon className="w-4 h-4" />
                          </div>

                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{step.name}</span>
                              {step.findings?.length > 0 && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
                                  {step.findings.length} findings
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                              <span>{step.tool}</span>
                              <span className="text-gray-700">•</span>
                              <span className="text-red-400">{step.mitre_id}</span>
                              {step.duration && (
                                <>
                                  <span className="text-gray-700">•</span>
                                  <span>{step.duration.toFixed(1)}s</span>
                                </>
                              )}
                            </div>
                          </div>

                          <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>

                        {/* Step Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 space-y-4 border-t border-gray-800 pt-4">
                            {/* Description */}
                            <p className="text-sm text-gray-400">{step.description}</p>

                            {/* MITRE Info */}
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-xs">
                                {step.tool}
                              </span>
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                                {step.mitre_id}: {step.mitre_name}
                              </span>
                            </div>

                            {/* Output */}
                            {step.output && (
                              <div>
                                <div className="text-xs font-medium text-gray-400 mb-2 flex items-center gap-2">
                                  <Terminal className="w-4 h-4" />
                                  Output
                                </div>
                                <pre className="p-3 bg-gray-900 rounded-lg text-xs text-green-400 font-mono overflow-x-auto max-h-48 overflow-y-auto">
                                  {step.output}
                                </pre>
                              </div>
                            )}

                            {/* AI Analysis */}
                            {step.ai_analysis && (
                              <div className="p-4 bg-brand-green/10 border border-brand-green/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-3">
                                  <Bot className="w-5 h-5 text-brand-green" />
                                  <span className="font-medium text-brand-green">Analyse IA</span>
                                </div>
                                <p className="text-sm text-gray-300 whitespace-pre-wrap">
                                  {step.ai_analysis}
                                </p>
                              </div>
                            )}

                            {/* Findings */}
                            {step.findings?.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-red-400 mb-2 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  Findings de Sécurité
                                </div>
                                <div className="space-y-2">
                                  {step.findings.map((finding, i) => (
                                    <div
                                      key={i}
                                      className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm"
                                    >
                                      {typeof finding === 'object' ? (
                                        <div>
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                              finding.severity === 'critical' ? 'bg-red-600 text-white' :
                                              finding.severity === 'high' ? 'bg-orange-500 text-white' :
                                              finding.severity === 'medium' ? 'bg-yellow-500 text-black' :
                                              'bg-blue-500 text-white'
                                            }`}>
                                              {finding.severity?.toUpperCase() || 'INFO'}
                                            </span>
                                            <span className="text-white font-medium">{finding.type || 'Finding'}</span>
                                          </div>
                                          <p className="text-red-300">{finding.description || JSON.stringify(finding)}</p>
                                        </div>
                                      ) : (
                                        <span className="text-red-300">{finding}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-brand-green/20 bg-brand-black-light flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {sessionId ? (
              <span className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
                Session: {sessionId.slice(0, 8)}...
              </span>
            ) : (
              <span>
                {attackChain.length} étapes d'attaque disponibles
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!sessionId ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Annuler
                </button>
                {simulationMode && (
                  <span className="px-2.5 py-1 text-xs font-medium bg-gray-700/60 text-gray-300 rounded-lg border border-gray-600">
                    SIMULATION
                  </span>
                )}
                {!simulationMode && (
                  <span className="px-2.5 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-lg border border-red-500/40">
                    MODE RÉEL
                  </span>
                )}
                <button
                  onClick={handleStart}
                  disabled={!dcIp}
                  className="px-6 py-2.5 bg-gradient-to-r from-brand-green to-green-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Lancer l'Attaque
                </button>
              </>
            ) : isRunning ? (
              <button
                onClick={handleStop}
                className="px-6 py-2.5 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <Square className="w-4 h-4" />
                Arrêter
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setSessionId(null);
                    setSessionStatus(null);
                    setShowConfig(true);
                  }}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Nouveau
                </button>
                <button
                  onClick={() => {
                    // Generate report - navigate to reports page with session data
                    const reportData = {
                      type: 'ad_attack',
                      sessionId: sessionId,
                      domain: sessionStatus?.target_domain,
                      summary: sessionStatus?.summary,
                      findings: sessionStatus?.steps?.flatMap(s => s.findings || []) || []
                    };
                    localStorage.setItem('pending_report', JSON.stringify(reportData));
                    window.location.href = '/reports?generate=ad_attack&session=' + sessionId;
                  }}
                  disabled={sessionStatus?.status !== 'completed'}
                  className={`px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    sessionStatus?.status === 'completed'
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  Générer le rapport
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 bg-brand-green text-white font-semibold rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Valider et Fermer
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ADAttackWizard;
